/**
 * The office's areas (docs/refactor/29): the main hall, the kitchen and the war room — and the Bug
 * Hunt, one more room of the same office.
 *
 * The hall is the room that existed before the areas: every agent at work is there, whatever its
 * person is doing. A person can walk their own character to the kitchen or the war room (right
 * click on it); they leave the hall, their agents do not — an agent that is working is in the hall
 * as a figure of its own, so the hall still shows every prompt being run.
 *
 * Pure functions, so the rules are tested without drawing a room.
 */

import { containmentBox, roomHeightFor, type FloorSize } from './layout'
import type { DeveloperEntity } from './store'
import type { ActivitySession, DeveloperActivity, RoomArea } from '../lib/types'

/** Where a viewer can be: an area of the office, or the Bug Hunt's room. */
export type Place = RoomArea | 'bugs'

/** In the order the arrows walk them, wrapping at both ends. */
export const PLACES: Place[] = ['hall', 'kitchen', 'war-room', 'bugs']

/** The areas a character can stand in. */
export const ROOM_AREAS: RoomArea[] = ['hall', 'kitchen', 'war-room']

/** The words for each place, as locale keys. The Bug Hunt keeps the name it has everywhere else. */
export const PLACE_KEYS: Record<Place, string> = {
  hall: 'office.hall',
  kitchen: 'office.kitchen',
  'war-room': 'office.warRoom',
  bugs: 'bugHunt.title',
}

/** The place before or after this one, wrapping: the arrows at the room's edges. */
export function stepPlace(current: Place, direction: -1 | 1): Place {
  const index = PLACES.indexOf(current)
  return PLACES[(index + direction + PLACES.length) % PLACES.length]!
}

/** An area from the address (`?area=kitchen`); the hall for anything else. */
export function areaFromParam(value: string | null | undefined): RoomArea {
  return ROOM_AREAS.includes(value as RoomArea) ? (value as RoomArea) : 'hall'
}

/** Where somebody's character stands. The server leaves it out for the hall. */
export function roomAreaOf(activity: Pick<DeveloperActivity, 'roomArea'> | undefined): RoomArea {
  return areaFromParam(activity?.roomArea)
}

/** The route for a place: the floor with its area, or the Bug Hunt. */
export function routeFor(place: Place): string {
  if (place === 'bugs') return '/track/bugs'
  return place === 'hall' ? '/track/floor' : `/track/floor?area=${place}`
}

/**
 * How tall the hall is drawn: as deep as its busiest zone needs (layout.ts, roomHeightFor). The
 * strips are the room's own (room/areas.ts): clear of the walls, and of the jar in the bugs zone.
 */
export function hallHeightFor(countsByZone: { bugs: number; features: number; others: number }, size: FloorSize): number {
  return Math.max(
    roomHeightFor(countsByZone.bugs, size, { left: 70, right: containmentBox(size).x - 40 }),
    roomHeightFor(countsByZone.features, size, { left: 70, right: 930 }),
    roomHeightFor(countsByZone.others, size, { left: 70, right: 930 }),
  )
}

/** A kitchen's or a war room's floor: clear of the furniture along each side. */
export const AREA_STRIP = { left: 90, right: 910 }

export interface AwayAgent {
  /** The figure in the hall: an agent standing in for its person, keyed by its session. */
  entity: DeveloperEntity
  ownerId: string
  /** The session as the activity endpoint reports it, for the hall zone and the card. */
  activity: ActivitySession | undefined
}

/**
 * The agents at work of the people who walked out of the hall: each one a figure of its own, so
 * the hall keeps showing every prompt being run.
 *
 * Drawn as an agent with a character (`isAgent`), keyed by its session id — the same id that picks
 * its look beside its person, so it looks the same in the hall as it would at their desk. The first
 * is "<name>'s agent", the others are numbered like clones (2_Alice).
 */
export function awayAgents(
  away: DeveloperEntity[],
  activityOf: (developerId: string) => DeveloperActivity | undefined,
  nameFor: (owner: string, index: number) => string,
): AwayAgent[] {
  const result: AwayAgent[] = []

  for (const person of away) {
    const busy = person.sessions.filter((session) => session.activityState !== 'Idle')
    const sessions = activityOf(person.id)?.sessions ?? []

    busy.forEach((session, index) => {
      result.push({
        ownerId: person.id,
        activity: sessions.find((s) => s.workSessionId === session.workSessionId),
        entity: {
          ...person,
          id: session.workSessionId,
          name: nameFor(person.name, index),
          isAgent: true,
          state: session.activityState,
          sessions: [session],
          messages: [],
        },
      })
    })
  }

  return result
}
